import React, { useState } from "react";
import { ArrowRight, Phone } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import img from "../Images/manual.webp"
import img1 from "../Images/pneumatic.webp"
import img2 from "../Images/nonreturn.png"
import img3 from "../Images/safety.jpg"
import img4 from "../Images/steam.webp"
import img5 from "../Images/stainers.webp"
import img6 from "../Images/manifold.webp"
import img7 from "../Images/flow.avif"
import img8 from "../Images/controllers.webp"

const SteamAccessories = () => {
  const [hoveredId, setHoveredId] = useState(null);

  const steamProducts = [
    {
      id: 1,
      name: "Manual Valves",
      description: "High-quality manual control valves for steam applications with precise flow control and durable construction for industrial environments.",
      imgSrc: img,
      link: "/products/manual-valves"
    },
    {
      id: 2,
      name: "Pneumatic Valves",
      description: "Automated pneumatic control valves for precise steam regulation with advanced actuation technology for optimal performance.",
      imgSrc: img1,
      link: "/products/pneumatic-valves"
    },
    {
      id: 3,
      name: "Non Return Valves",
      description: "Reliable check valves to prevent reverse flow in steam systems, ensuring safe and efficient operation.",
      imgSrc: img2,
      link: "/products/non-return-valves"
    },
    {
      id: 4,
      name: "Safety Valves",
      description: "Critical pressure relief valves for system protection, ASME certified for reliable overpressure protection.",
      imgSrc: img3,
      link: "/products/safety-valves"
    },
    {
      id: 5,
      name: "Steam Traps",
      description: "Efficient condensate removal devices for steam systems, maximizing energy efficiency and system performance.",
      imgSrc: img4,
      link: "/products/steam-traps"
    },
    {
      id: 6,
      name: "Strainers",
      description: "Filtration devices to protect steam system components from debris and contaminants, extending equipment life.",
      imgSrc: img5,
      link: "/products/strainers"
    },
    {
      id: 7,
      name: "Accessories",
      description: "Essential components for steam system installation and maintenance including fittings, gaskets, and mounting hardware.",
      imgSrc: img6,
      link: "/products/accessories"
    },
    {
      id: 8,
      name: "Flow Meters",
      description: "Accurate measurement devices for steam flow monitoring with digital displays and data logging capabilities.",
      imgSrc: img7,
      link: "/products/flow-meters"
    },
    {
      id: 9,
      name: "Other Products",
      description: "Additional steam system components and specialized equipment including controllers, sensors, and monitoring systems.",
      imgSrc: img8,
      link: "/products/others"
    }
  ];

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Steam System Accessories & Components",
    "description": "Comprehensive range of steam system components including valves, traps, strainers, and monitoring equipment",
    "itemListElement": steamProducts.map((product, index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": product.name,
      "description": product.description,
      "image": product.imgSrc,
      "url": `https://www.gorthermenergy.com${product.link}`,
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Gortherm Energy"
        }
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Steam Accessories & System Components | Valves, Traps & Flow Meters | Gortherm Energy</title>
        <meta name="description" content="Comprehensive range of steam system accessories including manual valves, pneumatic valves, safety valves, steam traps, strainers, flow meters, and essential components for industrial steam applications." />
        <meta name="keywords" content="steam accessories, steam valves, steam traps, safety valves, pneumatic valves, manual valves, check valves, strainers, flow meters, steam system components, industrial steam equipment, condensate removal, steam filtration Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Steam Accessories & System Components | Gortherm Energy" />
        <meta property="og:description" content="High-quality steam system components for optimal performance and reliability. Complete range of valves, traps, and monitoring equipment." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/steam-accessories" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Steam Accessories & System Components | Gortherm Energy" />
        <meta name="twitter:description" content="Industrial steam system accessories and components for efficient and safe operation." />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/steam-accessories" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <header className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-blue-900 opacity-50"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-900/50 to-transparent"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8">
            <div className="animate-fade-in-up">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 animate-slide-down">
                Steam Accessories & Components
              </h1>
              <div className="w-16 sm:w-20 h-1 bg-blue-400 mb-6 animate-slide-right"></div>
              <p className="text-lg sm:text-xl lg:text-2xl text-blue-100 max-w-2xl animate-fade-in">
                Discover our comprehensive range of high-quality steam system components 
                designed for optimal performance and reliability.
              </p>
            </div>
          </div>
          
          {/* Decorative diagonal lines */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg 
              className="w-full h-16 sm:h-24 fill-current text-gray-50" 
              viewBox="0 0 100 100" 
              preserveAspectRatio="none"
            >
              <polygon points="0,100 100,0 100,100" />
            </svg>
          </div>
        </header>

        {/* Products Grid Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center mb-8 sm:mb-12 animate-fade-in">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Product Range</h2>
            <p className="text-base sm:text-lg text-gray-600">
              Explore our wide selection of steam system components and accessories
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {steamProducts.map((product, index) => (
              <article
                key={product.id}
                onClick={() => window.location.href = product.link}
                onMouseEnter={() => setHoveredId(product.id)}
                onMouseLeave={() => setHoveredId(null)}
                className="bg-white rounded-xl shadow-sm hover:shadow-xl 
                         transform transition-all duration-300 
                         hover:-translate-y-2 cursor-pointer
                         animate-slide-up overflow-hidden"
                style={{
                  animationDelay: `${index * 100}ms`
                }}
                itemScope
                itemType="https://schema.org/Product"
              >
                <div className="w-full overflow-hidden">
                  <img
                    src={product.imgSrc}
                    alt={`${product.name} - Industrial steam system component by Gortherm Energy`}
                    className={`w-full h-48 sm:h-60 object-cover transition-transform duration-500
                              ${hoveredId === product.id ? 'scale-110' : 'scale-100'}`}
                    loading="lazy"
                    width="400"
                    height="300"
                    itemProp="image"
                  />
                </div>
                <div className="p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 transition-colors duration-300" itemProp="name">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm mb-4 line-clamp-2" itemProp="description">
                    {product.description}
                  </p>
                  <div className={`flex items-center text-blue-600 text-xs sm:text-sm font-medium
                                  transition-all duration-300
                                  ${hoveredId === product.id ? 'translate-x-2' : ''}`}>
                    <span className="mr-2">View Details</span>
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="bg-gray-50 py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 sm:mb-12 text-center">
              Why Choose Our Steam Accessories?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-600">Quality Assured</h3>
                <p className="text-sm sm:text-base text-gray-600">
                  All components meet international standards and undergo rigorous quality testing for reliable performance.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-600">Expert Support</h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Technical guidance and support from experienced steam system specialists for proper selection and installation.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-600">Comprehensive Range</h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Complete portfolio of steam system components from a single trusted supplier for streamlined procurement.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-600">Long Service Life</h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Durable construction and quality materials ensure extended operational life and minimal maintenance requirements.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <aside className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Need Help Selecting Components?
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Our steam system experts are ready to help you select the right accessories 
              and components for your specific application.
            </p>
            <a 
              href="tel:+9779802781400"
              className="inline-block"
              aria-label="Contact steam system experts at +977 980 278 1400"
            >
              <button className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold">
                <Phone className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                <span>Contact Our Experts</span>
              </button>
            </a>
            <p className="mt-4 text-sm sm:text-base text-gray-600">
              Call us at: <a href="tel:+9779802781400" className="text-blue-600 hover:text-blue-700 font-semibold">+977 980 278 1400</a>
            </p>
          </div>
        </aside>
      </main>

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-right {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animate-slide-down {
          animation: slide-down 0.6s ease-out forwards;
        }

        .animate-slide-right {
          animation: slide-right 0.6s ease-out forwards;
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .animate-slide-up {
          opacity: 0;
          animation: fade-in-up 0.6s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default SteamAccessories;